import { roleMap } from "./constants";
import { Class, GradeCriteria, HasId, Session, Level } from "./Dtos";

export const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0"); // Add leading zero
  const day = String(today.getDate()).padStart(2, "0"); // Add leading zero
  return `${year}-${month}-${day}`;
};

export const mapClass = (classes: Class[]): { [key: string]: string } => {
  const map: { [key: string]: string } = {};

  classes.forEach((cls) => {
    map[cls.id] = cls.className;
  });

  return map;
};

export const mapSession = (sessions: Session[]): { [key: string]: string } => {
  const map: { [key: string]: string } = {};

  sessions.forEach((s) => {
    map[s.id] = s.sessionName;
  });

  return map;
};

export function calculateAge(birthDateString: string) {
  const birthDate = new Date(birthDateString);

  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();

  const monthDifference = today.getMonth() - birthDate.getMonth();
  const dayDifference = today.getDate() - birthDate.getDate();

  if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
    age--;
  }

  return age;
}

export const getRandomColor = (seed: string) => {
  const styles = [
    "red",
    "orange",
    "yellow",
    "blue",
    "cyan",
    "purple",
    "pink",
    "brand",
    "gray",
  ];
  const randomStyle = styles[Math.floor(Math.random() * styles.length)];
  return randomStyle + "." + seed;
};

export const findById = <T extends HasId>(
  items: T[],
  id: string
): T | undefined => {
  return items.find((item) => item.id === id);
};

export const formatDate = (date?: string | Date): string => {
  if (!date) {
    return "";
  }
  const d = new Date(date);
  if (isNaN(d.getTime())) {
    return "";
  }

  // Using UTC methods to avoid timezone-related issues where the date might shift by a day.
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatDateTime = (date?: string | Date): string => {
  if (!date) {
    return "";
  }
  const d = new Date(date);
  if (isNaN(d.getTime())) {
    return "";
  }

  // Using local time methods to display the time in user's timezone
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${year}-${month}-${day} ${displayHours}:${minutes}:${seconds} ${period}`;
};

export const formatTimeAgo = (dateString: string) => {
  try {
    // Parse the date string from server (now with proper UTC format)
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "Invalid date";
    }

    // Get current time
    const now = new Date();

    // Calculate difference in seconds
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    // Handle negative time (future dates)
    if (diffInSeconds < 0) {
      return "Just now";
    }

    if (diffInSeconds < 60) {
      return "Just now";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    }
  } catch (error) {
    return "Unknown time";
  }
};

export function capitalizeFirstLetter(string: string) {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const getStudentClassNumberMessage = (count: number): string => {
  switch (count) {
    case 0:
      return "Only student in class";
    case 1:
      return "1 classmate";
    default:
      return `${count} classmates`;
  }
};

export const getColorForGrade = (
  gradeCriterias: GradeCriteria[],
  grade: string
): string => {
  const criteria = gradeCriterias.find(
    (c) => c.grade.toUpperCase() === grade.toUpperCase()
  );
  return criteria ? criteria.color : "gray";
};

function isObject(obj: any) {
  return obj !== null && typeof obj === "object";
}

export function deepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) {
    return true;
  }

  if (!isObject(obj1) || !isObject(obj2)) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

export function getNextOrder<T extends { order: number }>(
  collection: T[]
): number {
  if (collection.length === 0) return 1;

  const maxOrder = collection.reduce(
    (max, item) => (item.order > max ? item.order : max),
    collection[0].order
  );

  return maxOrder + 1;
}
export function isNullOrEmpty(value: string | null | undefined): boolean {
  return !value || value.trim() === "";
}

export function getInitials(name: string): string {
  const words = name
    .trim()
    .split(" ")
    .filter((word) => word.length > 0);
  let initials = "";

  for (let i = 0; i < words.length && i < 3; i++) {
    initials += words[i][0];
  }

  return initials.toUpperCase();
}

export function calculatePercentage(value: number, total: number) {
  if (total === 0) {
    return 0;
  }
  const percentage = (value / total) * 100;
  return Math.round(percentage);
}

export function getColorBasedOnPercentage(percentage: number | string) {
  if ((percentage as number) < 60) {
    return "red"; // For percentages below 70
  } else if (percentage === 100) {
    return "green"; // For percentages equal to 100
  } else {
    return "blue"; // For all other percentages
  }
}

export function getShortName(name: string): string {
  const words = name.split(" ").filter((word) => word.length > 0);

  const modifiedName = words
    .slice(0, 2)
    .map((word) => (word.length >= 3 ? word.substring(0, 3) : word))
    .join(" ");

  return modifiedName;
}

export const getUserAgentInfo = (userAgent: string) => {
  if (!userAgent) return { browser: "Unknown", device: "Unknown" };

  const ua = userAgent.toLowerCase();
  let browser = "Unknown";
  let device = "Desktop";

  // Browser detection
  if (ua.includes("chrome")) browser = "Chrome";
  else if (ua.includes("firefox")) browser = "Firefox";
  else if (ua.includes("safari")) browser = "Safari";
  else if (ua.includes("edge")) browser = "Edge";
  else if (ua.includes("opera")) browser = "Opera";

  // Device detection
  if (
    ua.includes("mobile") ||
    ua.includes("android") ||
    ua.includes("iphone")
  ) {
    device = "Mobile";
  } else if (ua.includes("tablet") || ua.includes("ipad")) {
    device = "Tablet";
  }

  return { browser, device };
};

const parseJsonValues = (jsonString: string) => {
  try {
    if (!jsonString) return null;
    return JSON.parse(jsonString);
  } catch {
    return null;
  }
};

export const getChangedProperties = (oldValues: string, newValues: string) => {
  const old = parseJsonValues(oldValues);
  const new_ = parseJsonValues(newValues);

  if (!old && !new_) return [];
  if (!old)
    return Object.keys(new_).map((key) => ({
      key,
      oldValue: null,
      newValue: new_[key],
    }));
  if (!new_)
    return Object.keys(old).map((key) => ({
      key,
      oldValue: old[key],
      newValue: null,
    }));

  const allKeys = [...new Set([...Object.keys(old), ...Object.keys(new_)])];
  const changes = [];

  for (const key of allKeys) {
    const oldValue = old[key];
    const newValue = new_[key];

    if (oldValue !== newValue) {
      changes.push({ key, oldValue, newValue });
    }
  }

  return changes;
};

export const getRoleLabel = (roleValue: number): string => {
  return roleMap[roleValue] || "Unknown";
};

export const getClassOptions = (
  classes: Class[],
  includeAllOption: boolean = false
): { label: string; value: string }[] => {
  const options = classes.map((cls) => ({
    label: cls.className,
    value: cls.id,
  }));

  if (includeAllOption) {
    return [{ label: "All Classes", value: "" }, ...options];
  }

  return options;
};

export const getLevelOptions = (
  levels: Level[],
  includeAllOption: boolean = false
): { label: string; value: string }[] => {
  const options = levels.map((l) => ({
    label: l.levelName,
    value: l.id,
  }));

  if (includeAllOption) {
    return [{ label: "All Levels", value: "" }, ...options];
  }

  return options;
};
